import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Download,
  BarChart3,
  FileText,
  Users,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { feeCollectionAPI, securitfeeAPI, formatCurrency, formatNumber } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { downloadBlob } from '../utils/csvExport';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);
const PAGE_SIZE = 50;

const ReportCollectionCheck = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    active_subscribers: 0,
    collected_count: 0,
    not_collected_count: 0,
    collected_amount: 0,
    not_collected_amount: 0,
    expected_amount: 0,
    collection_rate: 0,
  });
  const [byOperator, setByOperator] = useState([]);
  const [notCollected, setNotCollected] = useState([]);
  const [pagination, setPagination] = useState({ limit: PAGE_SIZE, offset: 0, total: 0 });
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportAllRows, setExportAllRows] = useState(null);

  useEffect(() => {
    securitfeeAPI.getByOperator({ month, year })
      .then((res) => {
        const raw = Array.isArray(res) ? res : (res?.data || []);
        setOperatorOptions(raw.map((r) => r.operatorName || r.operator_name || r.operator).filter(Boolean));
      })
      .catch(() => setOperatorOptions([]));
  }, [month, year]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters or page change
  }, [month, year, operatorFilter, page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        month,
        year,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (operatorFilter !== 'all') params.operator = operatorFilter;

      const res = await feeCollectionAPI.getCollectionCheck(params);
      const data = res?.data || {};

      setSummary(data.summary || {
        active_subscribers: 0,
        collected_count: 0,
        not_collected_count: 0,
        collected_amount: 0,
        not_collected_amount: 0,
        expected_amount: 0,
        collection_rate: 0,
      });

      setByOperator((data.by_operator || []).map((row) => ({
        operator: row.operator_name || row.operator,
        operator_id: row.operator_id,
        active_subscribers: row.active_subscribers || 0,
        collected_count: row.collected_count || 0,
        not_collected_count: row.not_collected_count || 0,
        collected_amount: row.collected_amount || 0,
        not_collected_amount: row.not_collected_amount || 0,
        expected_amount: row.expected_amount || 0,
        collection_rate: row.collection_rate || 0,
      })));
      setNotCollected(data.not_collected_subscribers || []);
      setPagination(data.pagination || { limit: PAGE_SIZE, offset: page * PAGE_SIZE, total: 0 });
    } catch (err) {
      console.error(err);
      setError(err.message || t('reportCollectionCheck.loadError'));
      setByOperator([]);
      setNotCollected([]);
    } finally {
      setLoading(false);
    }
  };

  const reportPeriodLabel = () => {
    return `${t(`common.monthNames.${month}`, { defaultValue: month })} ${year}`;
  };

  const handleOperatorDrilldown = (operatorName) => {
    setOperatorFilter(operatorName);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / PAGE_SIZE));
  const displayNotCollected = exportAllRows ?? notCollected;

  const fetchAllNotCollectedSubscribers = async () => {
    const total = pagination.total || 0;
    if (total === 0) return [];

    const batchSize = 5000;
    const all = [];
    for (let offset = 0; offset < total; offset += batchSize) {
      const params = {
        month,
        year,
        limit: Math.min(batchSize, total - offset),
        offset,
      };
      if (operatorFilter !== 'all') params.operator = operatorFilter;
      const res = await feeCollectionAPI.getCollectionCheck(params);
      const batch = res?.data?.not_collected_subscribers || [];
      all.push(...batch);
      if (batch.length === 0) break;
    }
    return all;
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const allRows = await fetchAllNotCollectedSubscribers();
      setExportAllRows(allRows);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const monthName = MONTHS.find((m) => m.value === month)?.label || 'Current';
      const filename = `collection-check-report-${monthName.toLowerCase()}-${year}`;
      const result = await PDFExporter.exportToPDF('report-collection-check', filename, { scale: 1.5 });
      if (!result.success) alert(t('common.exportPdfFailed') + ': ' + result.error);
    } catch (err) {
      alert(t('common.exportPdfFailed') + ': ' + (err.message || t('common.unknownError')));
    } finally {
      setExportAllRows(null);
      setIsExporting(false);
    }
  };

  const handleExportZip = async () => {
    setIsExporting(true);
    try {
      const params = { month, year };
      if (operatorFilter !== 'all') params.operator = operatorFilter;
      const { blob, filename } = await feeCollectionAPI.exportCollectionCheckZip(params);
      downloadBlob(filename, blob);
    } catch (err) {
      alert(t('common.exportZipFailed') + ': ' + (err.message || t('common.unknownError')));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div id="report-collection-check" className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 print:shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="h-8 w-8 text-primary-600" />
                {t('reportCollectionCheck.title')}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">{t('common.appBrand')}</p>
              <p className="text-sm text-gray-600 mt-2">
                {t('common.reportPeriod')}: <strong>{reportPeriodLabel()}</strong>
                {operatorFilter !== 'all' && <> · {t('common.operator')}: <strong>{operatorFilter}</strong></>}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('common.generated')}: {new Date().toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportZip}
                disabled={isExporting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="h-4 w-4" />
                {isExporting ? t('common.exporting') : t('common.exportZip')}
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
              >
                <Download className="h-4 w-4" />
                {isExporting ? t('common.exporting') : t('common.exportPDF')}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.month')}</label>
                <select
                  value={month}
                  onChange={(e) => { setMonth(Number(e.target.value)); setPage(0); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500"
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.year')}</label>
                <select
                  value={year}
                  onChange={(e) => { setYear(Number(e.target.value)); setPage(0); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.operator')}</label>
                <select
                  value={operatorFilter}
                  onChange={(e) => { setOperatorFilter(e.target.value); setPage(0); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">{t('common.allOperators')}</option>
                  {operatorOptions.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {t('common.reportPeriod')}: <strong className="text-gray-700">{reportPeriodLabel()}</strong>
            {operatorFilter !== 'all' && (
              <> · {t('common.operator')}: <strong className="text-gray-700">{operatorFilter}</strong></>
            )}
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-800">
            <span className="text-red-500 text-lg">{t('common.failedToLoad')}</span>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4" />
            <p className="text-gray-600">{t('reportCollectionCheck.loading')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary-100 rounded-lg">
                    <Users className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.activeSubscribers')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatNumber(summary.active_subscribers)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-green-100 rounded-lg">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.collected')}</p>
                    <p className="text-2xl font-bold text-green-700 tabular-nums">{formatCurrency(summary.collected_amount)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('operatorSubscribers.subscribersSuffix', { value: formatNumber(summary.collected_count) })}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-100 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.notCollected')}</p>
                    <p className="text-2xl font-bold text-amber-700 tabular-nums">{formatCurrency(summary.not_collected_amount)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('operatorSubscribers.subscribersSuffix', { value: formatNumber(summary.not_collected_count) })}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-blue-100 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.collectionRate')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{Number(summary.collection_rate || 0).toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">{t('reportCollectionCheck.summaryByOperator')}</h2>
                <span className="text-xs text-gray-500 ml-2">{t('reportCollectionCheck.clickToDrill')}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.operator')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.active')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.collected')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.notCollected')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('reportCollectionCheck.collectedAmount')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('reportCollectionCheck.notCollectedAmount')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.rate')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {byOperator.map((row) => (
                      <tr key={row.operator} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm font-medium">
                          <button
                            type="button"
                            onClick={() => handleOperatorDrilldown(row.operator)}
                            className={`text-left hover:underline ${operatorFilter === row.operator ? 'text-primary-700 font-semibold' : 'text-primary-600'}`}
                          >
                            {row.operator}
                          </button>
                        </td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(row.active_subscribers)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatNumber(row.collected_count)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatNumber(row.not_collected_count)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(row.collected_amount)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatCurrency(row.not_collected_amount)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{Number(row.collection_rate || 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                    {byOperator.length > 0 && operatorFilter === 'all' && (
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-5 py-3 text-sm text-gray-900">{t('common.total')}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums">{formatNumber(summary.active_subscribers)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatNumber(summary.collected_count)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatNumber(summary.not_collected_count)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(summary.collected_amount)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatCurrency(summary.not_collected_amount)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums">{Number(summary.collection_rate || 0).toFixed(1)}%</td>
                      </tr>
                    )}
                    {byOperator.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-5 py-10 text-center text-sm text-gray-500">
                          No data for the selected month and year.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  <h2 className="text-lg font-semibold text-gray-900">{t('reportCollectionCheck.subscribersNotCollected')}</h2>
                </div>
                <span className="text-sm text-gray-500">
                  {t('common.totalCount', { value: formatNumber(pagination.total || 0) })}
                  {operatorFilter !== 'all' && <> · {operatorFilter}</>}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.subscriberId')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.operator')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.msisdn')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.type')}</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.expected')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayNotCollected.map((row) => (
                      <tr key={`${row.subscriber_id}-${row.msisdn}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-900">{row.subscriber_id}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">{row.operator_name}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-900">{row.msisdn || row.phone_number || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">{row.subscriber_type || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-amber-700">{formatCurrency(row.expected_amount)}</td>
                      </tr>
                    ))}
                    {displayNotCollected.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-500">
                          {t('reportCollectionCheck.noNotCollected')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(pagination.total || 0) > PAGE_SIZE && !exportAllRows && (
                <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    {t('common.pageOf', { page: page + 1, total: totalPages })}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-gray-300 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('common.previous')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-gray-300 disabled:opacity-40"
                    >
                      {t('common.next')}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportCollectionCheck;
